import React, { useState, useEffect, useCallback, useRef, Suspense, lazy } from "react";
import "../../styles/components/management/Rota/Rota.css";
import FiltersBar from "./Rota/FiltersBar";
import RotaCalendar from "./Rota/RotaCalendar";
import { fetchRotaData } from "./Rota/api";

const API_BASE = process.env.REACT_APP_API_BASE;
const AssignModal = lazy(() => import("./Rota/AssignModal"));

function getInitialDateRange() {
  const today = new Date();
  const lastMonday = new Date(today);
  const day = today.getDay();
  const diffToMonday = day === 0 ? 6 : day - 1;
  lastMonday.setDate(today.getDate() - diffToMonday);

  const oneMonthLater = new Date(lastMonday);
  oneMonthLater.setMonth(lastMonday.getMonth() + 1);

  return {
    start: lastMonday.toISOString().slice(0, 10),
    end: oneMonthLater.toISOString().slice(0, 10),
  };
}

export default function Rota() {
  const [properties, setProperties] = useState([]);
  const [units, setUnits] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [users, setUsers] = useState([]);

  const [selectedProperty, setSelectedProperty] = useState("all");
  const [selectedUser, setSelectedUser] = useState("");
  const [dateRange, setDateRange] = useState(getInitialDateRange());

  const [currentMonth, setCurrentMonth] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [modalData, setModalData] = useState(null);
  const [, setNotification] = useState(null);

  const scrollRef = useRef(null);
  const calendarContainerRef = useRef(null);

  const loadData = useCallback(async () => {
    setLoadingMessage("Loading rota data...");
    setLoading(true);
    try {
      const rotaRes = await fetchRotaData(selectedProperty, dateRange);
      if (rotaRes.success) {
        setProperties(rotaRes.properties);
        setUnits(rotaRes.units);
        setBookings(rotaRes.bookings);
        setAssignments(rotaRes.assignments);
        setUsers(rotaRes.users);
        setCurrentMonth(
          new Date(dateRange.start).toLocaleString("default", {
            month: "long",
            year: "numeric",
          })
        );
      }
    } catch (err) {
      console.error("Error loading rota data:", err);
    } finally {
      setLoading(false);
    }
  }, [selectedProperty, dateRange]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Updated handleOpenModal with debug logs
  const handleOpenModal = (booking, unit, date) => {
    sessionStorage.setItem("pageScrollY", window.scrollY.toString());

    if (scrollRef.current) {
      sessionStorage.setItem("innerScrollTop", scrollRef.current.scrollTop.toString());
      sessionStorage.setItem("innerScrollLeft", scrollRef.current.scrollLeft.toString());
    }

    

    // Find property from properties list by PROPERTY_ID
    const property = properties.find((p) => p.PROPERTY_ID === unit.PROPERTY_ID);

 

    const propertyName = property ? property.PROPERTY_NAME : "Unknown Property";
    const unitName = unit?.UNIT_NAME || "Unknown Unit";


    setModalData({ booking, unit, date, propertyName, unitName });
  };

  // ... rest of your component remains unchanged

  const handleCloseModal = () => {
    setModalData(null);

    const pageScrollY = parseInt(sessionStorage.getItem("pageScrollY") || "0", 10);
    const innerScrollTop = parseInt(sessionStorage.getItem("innerScrollTop") || "0", 10);
    const innerScrollLeft = parseInt(sessionStorage.getItem("innerScrollLeft") || "0", 10);

    window.scrollTo({ top: pageScrollY, behavior: "instant" });

    const attemptScrollRestore = () => {
      const el = scrollRef.current;
      if (el) {
        el.scrollTo({
          top: innerScrollTop,
          left: innerScrollLeft,
          behavior: "smooth",
        });
      }
    };

    const observer = new MutationObserver(() => {
      if (scrollRef.current) {
        attemptScrollRestore();
        observer.disconnect();
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    setTimeout(() => {
      if (scrollRef.current) {
        attemptScrollRestore();
        observer.disconnect();
      }
    }, 1000);

    sessionStorage.removeItem("pageScrollY");
    sessionStorage.removeItem("innerScrollTop");
    sessionStorage.removeItem("innerScrollLeft");
  };

  const handleAssignmentSuccess = (message) => {
    loadData();
    setNotification(message);
    setTimeout(() => setNotification(null), 5000);
  };

  const handleReload = () => {
    setLoadingMessage("Updating bookings...");
    setLoading(true);
    setSuccessMsg("");

    if (selectedProperty === "all") {
      Promise.all(
        properties.map((prop) =>
          fetch(`${API_BASE}/app/API/rbmgt/bookings_by_property_v2.php?property_id=${encodeURIComponent(prop.PROPERTY_ID)}`)
            .then((res) => res.json())
            .then((data) => (data.success ? data.bookings : []))
            .catch(() => [])
        )
      )
        .then((all) => {
          const merged = all.flat();
          setBookings(merged);
          if (merged.length > 0) {
            setSuccessMsg("All bookings updated successfully.");
            setTimeout(() => setSuccessMsg(""), 3000);
          }
        })
        .finally(() => setLoading(false));
    } else {
      const someUnit = units.find((u) => u.PROPERTY_ID === selectedProperty);
      if (!someUnit) return;

      const today = new Date();
      const arrivalFrom = new Date(today);
      arrivalFrom.setDate(today.getDate() + 7);
      const arrivalTo = new Date(today);
      arrivalTo.setDate(today.getDate() + 21);

      fetch(`${API_BASE}/app/API/rbmgt/unit_bookings_v3.php?unit_id=${someUnit.UNIT_ID}&arrivalFrom=${arrivalFrom.toISOString().slice(0, 10)}&arrivalTo=${arrivalTo.toISOString().slice(0, 10)}&force=1`)
        .then(() =>
          fetch(`${API_BASE}/app/API/rbmgt/bookings_by_property_v2.php?property_id=${selectedProperty}`)
        )
        .then((res) => res.json())
        .then((data) => {
          if (data.success && data.bookings?.length > 0) {
            setBookings(data.bookings);
            setSuccessMsg("Bookings updated successfully.");
            setTimeout(() => setSuccessMsg(""), 3000);
          }
        })
        .catch(() => {
          setSuccessMsg("Failed to update bookings.");
        })
        .finally(() => setLoading(false));
    }
  };

  return (
    <div className="rota-container">
      <FiltersBar
        properties={properties}
        users={users}
        selectedProperty={selectedProperty}
        setSelectedProperty={setSelectedProperty}
        selectedUser={selectedUser}
        setSelectedUser={setSelectedUser}
        dateRange={dateRange}
        setDateRange={setDateRange}
        currentMonth={currentMonth}
        setCurrentMonth={setCurrentMonth}
        reload={handleReload}
      />

      <div ref={calendarContainerRef} style={{ position: "relative" }}>
        {loading && (
          <div className="loading-overlay">
            <div className="spinner" />
            <span>{loadingMessage}</span>
          </div>
        )}

        {successMsg && (
          <div className="success-overlay">
            <div className="confirmation-text">{successMsg}</div>
          </div>
        )}

        <RotaCalendar
          bookings={bookings}
          assignments={assignments}
          users={users}
          units={units}
          properties={properties}
          selectedProperty={selectedProperty}
          selectedUser={selectedUser}
          dateRange={dateRange}
          openModal={handleOpenModal}
          colorSettings={{}}
          scrollRef={scrollRef}
        />
      </div>

      <Suspense fallback={null}>
        {modalData && (
          <AssignModal
            {...modalData} // booking, unit, date, propertyName, unitName
            users={users}
            bookings={bookings}
            assignments={assignments}
            units={units}
            dateRange={dateRange}
            onClose={handleCloseModal}
            onSuccess={handleAssignmentSuccess}
          />
        )}
      </Suspense>
    </div>
  );
}
